import {ISearchInputRegion} from "../components/ISearchInputValue";
import {ISearchFiltersFormValues} from "../types/ISearchFiltersFormValues";
import {SearchFormValuesType} from "../types/SearchFormValuesType";
import {SearchTab} from "../types/SearchTab";
import {dispatchStorageChange} from "./storage_change_event";

export const RECENT_FILTERS_STORAGE_KEY = "rp_recent_search_filters";
export const MAX_RECENT_FILTERS = 5;
const DEFAULT_FILTERS_DISPLAY_LIMIT = 1;

export interface IRecentFilter {
    filters: Partial<ISearchFiltersFormValues>;
    search?: SearchFormValuesType["search"];
    timestamp: number;
}

const getRecentFilterKey = (item: IRecentFilter): string => JSON.stringify({filters: item.filters, search: item.search});

/**
 * Saves filters to localStorage. Keeps up to 5 most recent searches.
 * New search is prepended; duplicates (same filters + search) are removed, keeping the newest.
 */
export const saveRecentFilters = (formValues: SearchFormValuesType): void => {
    try {
        const cleanedFilters: Partial<ISearchFiltersFormValues> = {};

        if (hasRangeValue(formValues.price)) {
            cleanedFilters.price = formValues.price;
        }

        if (hasRangeValue(formValues.rooms)) {
            cleanedFilters.rooms = formValues.rooms;
        }

        if (hasRangeValue(formValues.area)) {
            cleanedFilters.area = formValues.area;
        }

        if (formValues.instalment && formValues.instalment !== "") {
            cleanedFilters.instalment = formValues.instalment;
        }

        if (Array.isArray(formValues.type) && formValues.type.length > 0) {
            cleanedFilters.type = formValues.type;
        }

        if (Array.isArray(formValues.construction_end_date) && formValues.construction_end_date.length > 0) {
            cleanedFilters.construction_end_date = formValues.construction_end_date;
        }

        const hasFilters = Object.keys(cleanedFilters).length > 0 || formValues.search;

        if (!hasFilters) {
            return;
        }

        const newFilter: IRecentFilter = {
            filters: cleanedFilters,
            search: formValues.search,
            timestamp: Date.now()
        };

        const existing = getStoredRecentFilters();
        const merged = [newFilter, ...existing].filter(
            (item, index, arr) => arr.findIndex((x) => getRecentFilterKey(x) === getRecentFilterKey(item)) === index
        );
        const toStore = merged.slice(0, MAX_RECENT_FILTERS);

        localStorage.setItem(RECENT_FILTERS_STORAGE_KEY, JSON.stringify(toStore));
    } catch (error) {
        console.error("Failed to save recent filters:", error);
    }
};

/** A region search without filters - the only kind `saveRecentRegionSearch` may replace or evict. */
const isLocationOnlyRecentFilter = (item: IRecentFilter): boolean => Object.keys(item.filters ?? {}).length === 0 && item.search?.tabType === SearchTab.Regions;

/** Sorted region ids of a region search ("3766,20284"); "" for other searches. */
const getRegionIdsKey = (item: IRecentFilter): string => {
    const regions: (Partial<ISearchInputRegion> | null)[] = item.search?.tabType === SearchTab.Regions ? (item.search.regions ?? []) : [];

    return regions
        .map((region) => Number(region?.id))
        .sort((a, b) => a - b)
        .join(",");
};

/** Index of the oldest location-only entry, never the new one at index 0; -1 when there is none. */
const findOldestLocationOnlyIndex = (items: IRecentFilter[]): number => {
    for (let index = items.length - 1; index > 0; index--) {
        if (isLocationOnlyRecentFilter(items[index])) {
            return index;
        }
    }

    return -1;
};

/**
 * Saves a region picked outside the main search (the prices search) in the main search format. Unlike
 * `saveRecentFilters` it never evicts a search with filters: when the list is full it drops the oldest location-only
 * entry, and when every other entry has filters it gives up instead. Same-tab subscribers are notified.
 */
export const saveRecentRegionSearch = (region: ISearchInputRegion): boolean => {
    try {
        const entry: IRecentFilter = {
            filters: {},
            search: {label: region.full_name_reverted, tabType: SearchTab.Regions, regions: [region]},
            timestamp: Date.now()
        };
        const regionIdsKey = getRegionIdsKey(entry);
        // The full stored history - getRecentFilters() defaults to the single entry the search dropdown shows.
        const merged = [entry, ...getStoredRecentFilters().filter((item) => !(isLocationOnlyRecentFilter(item) && getRegionIdsKey(item) === regionIdsKey))];

        while (merged.length > MAX_RECENT_FILTERS) {
            const evictIndex = findOldestLocationOnlyIndex(merged);

            if (evictIndex === -1) {
                return false;
            }

            merged.splice(evictIndex, 1);
        }

        localStorage.setItem(RECENT_FILTERS_STORAGE_KEY, JSON.stringify(merged));
        dispatchStorageChange(RECENT_FILTERS_STORAGE_KEY);

        return true;
    } catch (error) {
        console.error("Failed to save recent region search:", error);

        return false;
    }
};

/**
 * Checks if range has values
 */
export const hasRangeValue = (range: {lower: string | number | ""; upper: string | number | ""} | undefined): boolean => {
    return !!range && (range.lower !== "" || range.upper !== "");
};

/**
 * Reads the full stored list from localStorage (up to 5 entries), newest first.
 * Used internally by saveRecentFilters to merge/dedup without losing background history.
 * Supports both legacy single-object and new array format.
 */
const getStoredRecentFilters = (): IRecentFilter[] => {
    if (typeof window === "undefined") {
        return [];
    }

    try {
        const stored = localStorage.getItem(RECENT_FILTERS_STORAGE_KEY);

        if (!stored) {
            return [];
        }

        const parsed = JSON.parse(stored) as IRecentFilter | IRecentFilter[];

        if (Array.isArray(parsed)) {
            return parsed;
        }

        return parsed.filters || parsed.search ? [parsed] : [];
    } catch (error) {
        console.error("Failed to get recent filters:", error);

        return [];
    }
};

/**
 * Gets the most recent filters from localStorage, newest first.
 * Defaults to the single most recent one (search dropdown display); pass
 * `MAX_RECENT_FILTERS` to get the full stored history (homepage personalization).
 */
export const getRecentFilters = (limit: number = DEFAULT_FILTERS_DISPLAY_LIMIT): IRecentFilter[] => {
    return getStoredRecentFilters().slice(0, limit);
};

/**
 * Gets the most recent filter (first from list). For backward compatibility.
 */
export const getRecentFilter = (): IRecentFilter | null => {
    const list = getRecentFilters();
    return list.length > 0 ? list[0] : null;
};
import {ISearchInputRegion} from "../components/ISearchInputValue";
import {ISearchFiltersFormValues} from "../types/ISearchFiltersFormValues";
import {SearchFormValuesType} from "../types/SearchFormValuesType";
import {SearchTab} from "../types/SearchTab";
import {dispatchStorageChange} from "./storage_change_event";

export const RECENT_FILTERS_STORAGE_KEY = "rp_recent_search_filters";
export const MAX_RECENT_FILTERS = 5;
const DEFAULT_FILTERS_DISPLAY_LIMIT = 1;

export interface IRecentFilter {
    filters: Partial<ISearchFiltersFormValues>;
    search?: SearchFormValuesType["search"];
    timestamp: number;
}

const getRecentFilterKey = (item: IRecentFilter): string => JSON.stringify({filters: item.filters, search: item.search});

/**
 * Saves filters to localStorage. Keeps up to 5 most recent searches.
 * New search is prepended; duplicates (same filters + search) are removed, keeping the newest.
 */
export const saveRecentFilters = (formValues: SearchFormValuesType): void => {
    try {
        const cleanedFilters: Partial<ISearchFiltersFormValues> = {};

        if (hasRangeValue(formValues.price)) {
            cleanedFilters.price = formValues.price;
        }

        if (hasRangeValue(formValues.rooms)) {
            cleanedFilters.rooms = formValues.rooms;
        }

        if (hasRangeValue(formValues.area)) {
            cleanedFilters.area = formValues.area;
        }

        if (formValues.instalment && formValues.instalment !== "") {
            cleanedFilters.instalment = formValues.instalment;
        }

        if (Array.isArray(formValues.type) && formValues.type.length > 0) {
            cleanedFilters.type = formValues.type;
        }

        if (Array.isArray(formValues.construction_end_date) && formValues.construction_end_date.length > 0) {
            cleanedFilters.construction_end_date = formValues.construction_end_date;
        }

        const hasFilters = Object.keys(cleanedFilters).length > 0 || formValues.search;

        if (!hasFilters) {
            return;
        }

        const newFilter: IRecentFilter = {
            filters: cleanedFilters,
            search: formValues.search,
            timestamp: Date.now()
        };

        const existing = getStoredRecentFilters();
        const merged = [newFilter, ...existing].filter(
            (item, index, arr) => arr.findIndex((x) => getRecentFilterKey(x) === getRecentFilterKey(item)) === index
        );
        const toStore = merged.slice(0, MAX_RECENT_FILTERS);

        localStorage.setItem(RECENT_FILTERS_STORAGE_KEY, JSON.stringify(toStore));
    } catch (error) {
        console.error("Failed to save recent filters:", error);
    }
};

/** A region search without filters - the only kind `saveRecentRegionSearch` may replace or evict. */
const isLocationOnlyRecentFilter = (item: IRecentFilter): boolean => Object.keys(item.filters ?? {}).length === 0 && item.search?.tabType === SearchTab.Regions;

/** Sorted region ids of a region search ("3766,20284"); "" for other searches. */
const getRegionIdsKey = (item: IRecentFilter): string => {
    const regions: (Partial<ISearchInputRegion> | null)[] = item.search?.tabType === SearchTab.Regions ? (item.search.regions ?? []) : [];

    return regions
        .map((region) => Number(region?.id))
        .sort((a, b) => a - b)
        .join(",");
};

/** Index of the oldest location-only entry, never the new one at index 0; -1 when there is none. */
const findOldestLocationOnlyIndex = (items: IRecentFilter[]): number => {
    for (let index = items.length - 1; index > 0; index--) {
        if (isLocationOnlyRecentFilter(items[index])) {
            return index;
        }
    }

    return -1;
};

/**
 * Saves a region picked outside the main search (the prices search) in the main search format. Unlike
 * `saveRecentFilters` it never evicts a search with filters: when the list is full it drops the oldest location-only
 * entry, and when every other entry has filters it gives up instead. Same-tab subscribers are notified.
 */
export const saveRecentRegionSearch = (region: ISearchInputRegion): boolean => {
    try {
        const entry: IRecentFilter = {
            filters: {},
            search: {label: region.full_name_reverted, tabType: SearchTab.Regions, regions: [region]},
            timestamp: Date.now()
        };
        const regionIdsKey = getRegionIdsKey(entry);
        // The full stored history - getRecentFilters() defaults to the single entry the search dropdown shows.
        const merged = [entry, ...getStoredRecentFilters().filter((item) => !(isLocationOnlyRecentFilter(item) && getRegionIdsKey(item) === regionIdsKey))];

        while (merged.length > MAX_RECENT_FILTERS) {
            const evictIndex = findOldestLocationOnlyIndex(merged);

            if (evictIndex === -1) {
                return false;
            }

            merged.splice(evictIndex, 1);
        }

        localStorage.setItem(RECENT_FILTERS_STORAGE_KEY, JSON.stringify(merged));
        dispatchStorageChange(RECENT_FILTERS_STORAGE_KEY);

        return true;
    } catch (error) {
        console.error("Failed to save recent region search:", error);

        return false;
    }
};

/**
 * Checks if range has values
 */
export const hasRangeValue = (range: {lower: string | number | ""; upper: string | number | ""} | undefined): boolean => {
    return !!range && (range.lower !== "" || range.upper !== "");
};

/**
 * Reads the full stored list from localStorage (up to 5 entries), newest first.
 * Used internally by saveRecentFilters to merge/dedup without losing background history.
 * Supports both legacy single-object and new array format.
 */
const getStoredRecentFilters = (): IRecentFilter[] => {
    if (typeof window === "undefined") {
        return [];
    }

    try {
        const stored = localStorage.getItem(RECENT_FILTERS_STORAGE_KEY);

        if (!stored) {
            return [];
        }

        const parsed = JSON.parse(stored) as IRecentFilter | IRecentFilter[];

        if (Array.isArray(parsed)) {
            return parsed;
        }

        return parsed.filters || parsed.search ? [parsed] : [];
    } catch (error) {
        console.error("Failed to get recent filters:", error);

        return [];
    }
};

/**
 * Gets the most recent filters from localStorage, newest first.
 * Defaults to the single most recent one (search dropdown display); pass
 * `MAX_RECENT_FILTERS` to get the full stored history (homepage personalization).
 */
export const getRecentFilters = (limit: number = DEFAULT_FILTERS_DISPLAY_LIMIT): IRecentFilter[] => {
    return getStoredRecentFilters().slice(0, limit);
};

/**
 * Gets the most recent filter (first from list). For backward compatibility.
 */
export const getRecentFilter = (): IRecentFilter | null => {
    const list = getRecentFilters();
    return list.length > 0 ? list[0] : null;
};
